// src/lib/solanaWallet.js
//
// Solana wallet connection for the browser, WITHOUT any extra npm package: it
// talks to the wallet the page already has (Phantom, Solflare, Backpack all inject
// a provider with connect / signMessage / disconnect). Step 2 of the Solana port
// only needs "connect" and "sign a login message"; building and sending
// transactions (step 3 onwards) will add @solana/web3.js.
//
// The object returned by connectSolanaWallet() has the same shape the EVM path
// produces in components/web3modal (address, signer, chainId, chain, ...), so the
// rest of the login flow (utils/walletAuth.js -> /api/user/walletlogin) runs as is:
// walletAuth only calls `signer.signMessage(text)` and sends the result back, and
// for Solana that result is the ed25519 signature as base64.

import { getChain } from "../config/chains";

const REMEMBER_KEY = "eeap-solana-wallet";

// Wallets in the order we try them when the user has not chosen one yet.
const WALLETS = [
  {
    name: "Phantom",
    get: () => {
      if (typeof window === "undefined") return null;
      if (window.phantom && window.phantom.solana && window.phantom.solana.isPhantom) return window.phantom.solana;
      if (window.solana && window.solana.isPhantom) return window.solana;
      return null;
    },
  },
  {
    name: "Solflare",
    get: () => (typeof window !== "undefined" && window.solflare && window.solflare.isSolflare ? window.solflare : null),
  },
  {
    name: "Backpack",
    get: () => (typeof window !== "undefined" && window.backpack && window.backpack.isBackpack ? window.backpack : null),
  },
];

const remember = (name) => {
  try { window.localStorage.setItem(REMEMBER_KEY, name); } catch (e) {}
};
const forget = () => {
  try { window.localStorage.removeItem(REMEMBER_KEY); } catch (e) {}
};
const remembered = () => {
  try { return window.localStorage.getItem(REMEMBER_KEY) || ""; } catch (e) { return ""; }
};

// True when the user connected a Solana wallet earlier and did not log out
// (used by App.js to restore the connection on page load).
export const wasSolanaConnected = () => !!remembered();

// { name, provider } for the wallet to use: the remembered one first, otherwise
// the first installed. null when no Solana wallet is installed.
const findSolanaWallet = () => {
  const wanted = remembered();
  const order = WALLETS.slice().sort((a, b) => (a.name === wanted ? -1 : b.name === wanted ? 1 : 0));
  for (const w of order) {
    const provider = w.get();
    if (provider) return { name: w.name, provider };
  }
  return null;
};


// Uint8Array -> base64 (the form the backend expects for a Solana signature).
const toBase64 = (bytes) => {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
};

// Connects to the wallet.
//   silent: only succeed if the site is already approved (no popup). Used to restore
//           a connection on page load and after an account change.
// Returns { address, signer, wallet, provider } or null when the user closed the
// popup / a silent connect was not possible. Throws only when no wallet is installed.
export const connectSolanaWallet = async ({ silent = false, chainId = null } = {}) => {
  const found = findSolanaWallet();
  if (!found) {
    const err = new Error("No Solana wallet found. Install Phantom or Solflare and reload the page.");
    err.code = "NO_SOLANA_WALLET";
    throw err;
  }
  const { name, provider } = found;

  let resp;
  try {
    resp = await provider.connect(silent ? { onlyIfTrusted: true } : undefined);
  } catch (e) {
    return null; // user rejected, or (silent) the site is not approved yet
  }
  const pk = (resp && resp.publicKey) || provider.publicKey;
  if (!pk) return null;
  const address = pk.toString();
  remember(name);

  const signer = {
    address,
    // Same contract as an ethers Signer: text in, signature out. Here the
    // signature is ed25519 over the UTF-8 bytes, returned as base64.
    signMessage: async (message) => {
      const bytes = new TextEncoder().encode(message);
      const res = await provider.signMessage(bytes, "utf8");
      const sig = res && res.signature ? res.signature : res;
      return toBase64(sig);
    },
  };

  const chain = getChain(chainId);
  return { address, signer, wallet: name, provider, chain };
};

export const disconnectSolanaWallet = async () => {
  const found = findSolanaWallet();
  forget();
  if (found && typeof found.provider.disconnect === "function") {
    try { await found.provider.disconnect(); } catch (e) {}
  }
};

// Calls `cb(newAddressOrNull)` when the user switches account in the wallet or
// disconnects. Returns an unsubscribe function (a no-op when no wallet exists).
export const onSolanaAccountChanged = (cb) => {
  const found = findSolanaWallet();
  if (!found || typeof found.provider.on !== "function") return () => {};
  const { provider } = found;
  const changed = (pk) => cb(pk ? pk.toString() : null);
  const gone = () => cb(null);
  provider.on("accountChanged", changed);
  provider.on("disconnect", gone);
  return () => {
    try {
      if (typeof provider.removeListener === "function") {
        provider.removeListener("accountChanged", changed);
        provider.removeListener("disconnect", gone);
      }
    } catch (e) {}
  };
};

// SOL balance through plain JSON-RPC (no library). Returns a number in SOL.
export const getSolanaBalance = async (address, rpcUrl) => {
  const res = await fetch(rpcUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "getBalance", params: [address, { commitment: "confirmed" }] }),
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error.message || "Solana RPC error");
  return Number(json.result.value) / 1e9;
};

// ---------------------------------------------------------------- signing a transaction the backend built
//
// The backend builds the transaction and signs its own parts (fee payer); the wallet only has to sign the
// transaction. No @solana/web3.js is needed in the browser: Phantom's `signTransaction` request
// accepts the serialized transaction as base58 (its parameter is named `message`) and returns the signature (base58) and the signing public key.
// Only Phantom supports this request; other Solana wallets are refused with a clear message.

const B58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

const base58Encode = (bytes) => {
  const digits = [];
  for (let i = 0; i < bytes.length; i += 1) {
    let carry = bytes[i];
    for (let j = 0; j < digits.length; j += 1) {
      carry += digits[j] << 8;
      digits[j] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }
  let out = "";
  for (let k = 0; k < bytes.length && bytes[k] === 0; k += 1) out += "1";
  for (let q = digits.length - 1; q >= 0; q -= 1) out += B58[digits[q]];
  return out;
};

const base64ToBytes = (b64) => {
  const bin = window.atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
};

const base58Decode = (str) => {
  const bytes = [];
  for (let i = 0; i < str.length; i += 1) {
    const v = B58.indexOf(str[i]);
    if (v < 0) throw new Error("Invalid base58 text");
    let carry = v;
    for (let j = 0; j < bytes.length; j += 1) {
      carry += bytes[j] * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  for (let k = 0; k < str.length && str[k] === "1"; k += 1) bytes.push(0);
  return Uint8Array.from(bytes.reverse());
};

const toBytes = (v) => {
  if (v instanceof Uint8Array) return v;
  if (Array.isArray(v)) return Uint8Array.from(v);
  if (typeof v === "string") {
    try { return base58Decode(v); } catch (e) { return base64ToBytes(v); }
  }
  return null;
};

// A wallet may hand back the whole SIGNED transaction instead of just the signature. This pulls the
// signature of `address` out of a legacy transaction's wire bytes:
//   [compact-u16 count][64-byte signatures...][message: 3-byte header, compact-u16 key count, 32-byte keys...]
const signatureFromSignedTx = (txBytes, address) => {
  const count = txBytes[0];
  if (count >= 128) throw new Error("Unexpected transaction format from the wallet.");
  let msg = 1 + 64 * count;
  if (txBytes[msg] >= 128) msg += 1; // versioned (v0) message: one version byte (0x80 | version) comes first
  const required = txBytes[msg];
  if (required >= 128) throw new Error("Unexpected transaction format from the wallet.");
  const keyCount = txBytes[msg + 3];
  const keysAt = msg + 4;
  const want = base58Decode(address);
  for (let i = 0; i < Math.min(required, keyCount); i += 1) {
    const k = txBytes.slice(keysAt + 32 * i, keysAt + 32 * (i + 1));
    if (k.length === 32 && k.every((b, j) => b === want[j])) {
      const sig = txBytes.slice(1 + 64 * i, 1 + 64 * (i + 1));
      if (sig.length === 64 && sig.some((b) => b !== 0)) return sig;
    }
  }
  throw new Error("The wallet's signature was not found in the signed transaction.");
};

// Position of `address` among the transaction's required signers (legacy transaction wire bytes), or -1.
const signerIndexInTx = (txBytes, address) => {
  const count = txBytes[0];
  if (count >= 128) return -1;
  let msg = 1 + 64 * count;
  if (txBytes[msg] >= 128) msg += 1; // v0 message: skip the version byte
  const required = txBytes[msg];
  if (required >= 128) return -1;
  const keyCount = txBytes[msg + 3];
  const keysAt = msg + 4;
  const want = base58Decode(address);
  for (let i = 0; i < Math.min(required, keyCount); i += 1) {
    const k = txBytes.slice(keysAt + 32 * i, keysAt + 32 * (i + 1));
    if (k.length === 32 && k.every((b, j) => b === want[j])) return i;
  }
  return -1;
};

// Phantom may add its own instructions before signing; when the message it signed differs from the one we sent, it is
// kept here (base64) so the app can pass it to the backend, which checks it and signs the final version.
let lastSignedMessage = null;
export const takeSignedMessage = () => { const m = lastSignedMessage; lastSignedMessage = null; return m; };
const captureSignedMessage = (reply, rawSent) => {
  lastSignedMessage = null;
  try {
    const sentMsg = rawSent.slice(1 + rawSent[0] * 64);
    let got = null;
    if (reply && reply.message && typeof reply.message.serialize === "function") got = reply.message.serialize();
    else if (reply && typeof reply.serialize === "function") { const f = reply.serialize(); got = f.slice(1 + f[0] * 64); }
    if (!got) {  return; } // eslint-disable-line no-console
    const same = got.length === sentMsg.length && got.every((b, i) => b === sentMsg[i]);
    // eslint-disable-next-line no-console
    if (!same) lastSignedMessage = toBase64(got);
  } catch (e) {  } // eslint-disable-line no-console
};

// `transactionBase64`: the serialized transaction (backend signatures filled in, the wallet's slot empty).
// Resolves the wallet's signature as base64 (what the backend accepts), or throws with a user-readable message.

// ---- debugging: a readable summary of the transaction we hand to Phantom, printed to the console ----
const describeTx = (raw) => {
  try {
    let i = 0;
    const nSig = raw[i++];
    i += nSig * 64;
    const v0 = (raw[i] & 0x80) !== 0;
    if (v0) i++;
    const header = [raw[i], raw[i + 1], raw[i + 2]];
    i += 3;
    const readShort = () => { let v = 0, sh = 0; for (;;) { const b = raw[i++]; v |= (b & 0x7f) << sh; if (!(b & 0x80)) break; sh += 7; } return v; };
    const nKeys = readShort();
    const keys = [];
    for (let k = 0; k < nKeys; k++) { keys.push(base58Encode(raw.slice(i, i + 32))); i += 32; }
    i += 32; // blockhash
    const nIx = readShort();
    const ixs = [];
    for (let k = 0; k < nIx; k++) {
      const prog = raw[i++];
      const na = readShort(); i += na;
      const dl = readShort(); i += dl;
      ixs.push({ program: keys[prog] || "alt#" + prog, accounts: na, dataBytes: dl });
    }
    let tables = 0, altWritable = 0, altReadonly = 0;
    if (v0) {
      tables = readShort();
      for (let t = 0; t < tables; t++) { i += 32; const w = readShort(); i += w; const r = readShort(); i += r; altWritable += w; altReadonly += r; }
    }
    return { bytes: raw.length, version: v0 ? 0 : "legacy", signatures: nSig, header, staticAccounts: nKeys, signers: keys.slice(0, header[0]), instructions: ixs, lookupTables: tables, altWritable, altReadonly, totalAccounts: nKeys + altWritable + altReadonly, parsedTo: i };
  } catch (e) { return { error: String(e && e.message) }; }
};
const dumpErr = (label, e) => {
  let json = "";
  try { json = JSON.stringify(e, Object.getOwnPropertyNames(e || {})).slice(0, 1500); } catch (x) {}
  // eslint-disable-next-line no-console
  console.error(label, "\n" + JSON.stringify({ name: e && e.name, message: e && e.message, code: e && e.code, data: e && e.data, json }), "\n" + String(e && e.stack), e);
};


// ---- Wallet Standard: the wallet signs the raw transaction bytes through the standard "solana:signTransaction" feature.
// Unlike window.solana.signTransaction(tx) it needs no Transaction object, and wallets take a different (simpler) code
// path for it, which is what we use for versioned (v0) transactions that carry a lookup table.
const stdWallets = () =>
  new Promise((resolve) => {
    const found = [];
    const api = { register: (...ws) => { found.push(...ws); return () => {}; } };
    const onRegister = (e) => { try { e.detail(api); } catch (x) { /* ignore */ } };
    try {
      window.addEventListener("wallet-standard:register-wallet", onRegister);
      window.dispatchEvent(new CustomEvent("wallet-standard:app-ready", { detail: api }));
    } catch (e) { /* no wallet standard */ }
    setTimeout(() => { try { window.removeEventListener("wallet-standard:register-wallet", onRegister); } catch (e) {} resolve(found); }, 250);
  });

const signWithWalletStandard = async (raw, address, preferName) => {
  const all = await stdWallets();
  const usable = all.filter((w) => w && w.features && w.features["solana:signTransaction"]);
  // eslint-disable-next-line no-console
  console.log("[solana] DEBUG wallet-standard wallets", JSON.stringify(all.map((w) => ({ name: w.name, accounts: (w.accounts || []).length, signTransaction: !!(w.features && w.features["solana:signTransaction"]), chains: w.chains }))));
  const pick = usable.find((w) => w.name === preferName) || usable.find((w) => (w.accounts || []).some((a) => a.address === address)) || usable[0];
  if (!pick) return null;
  let account = (pick.accounts || []).find((a) => a.address === address);
  if (!account && pick.features["standard:connect"]) {
    await pick.features["standard:connect"].connect({ silent: true });
    account = (pick.accounts || []).find((a) => a.address === address);
  }
  if (!account) throw new Error(`${pick.name} is not connected to ${address}.`);
  const chain = getChain() && getChain().chainId === 900002 ? "solana:mainnet" : "solana:devnet";
  // eslint-disable-next-line no-console
  const out = await pick.features["solana:signTransaction"].signTransaction({ account, transaction: raw, chain });
  const first = Array.isArray(out) ? out[0] : out;
  const signed = first && (first.signedTransaction || first.transaction);
  if (!signed) throw new Error("The wallet returned no signed transaction.");
  return signed instanceof Uint8Array ? signed : new Uint8Array(signed);
};

export const signSolanaTransactionMessage = async (transactionBase64, expectedAddress) => {
  const found = findSolanaWallet();
  if (!found) throw new Error("No Solana wallet found. Install Phantom and reload the page.");
  if (found.name !== "Phantom" || typeof found.provider.request !== "function") {
    throw new Error("Creating your identity needs Phantom for now. Open this page with Phantom connected.");
  }
  const { provider } = found;
  const signer = (provider.publicKey && provider.publicKey.toString()) || expectedAddress;
  if (!provider.publicKey || (expectedAddress && signer !== expectedAddress)) {
    throw new Error("Phantom is connected to a different account than the one on your profile. Switch accounts in Phantom and try again.");
  }
  // The backend keeps its own (platform) signature and adds ours later, so Phantom gets the transaction with
  // every signature slot blank: a partially signed versioned transaction can make Phantom fail while signing.
  {
    const rawIn = base64ToBytes(transactionBase64);
    // eslint-disable-next-line no-console
    console.log("[solana] DEBUG provider", JSON.stringify({ name: found.name, isPhantom: !!provider.isPhantom, connected: provider.isConnected, publicKey: provider.publicKey && provider.publicKey.toString(), expectedAddress, hasSignTransaction: typeof provider.signTransaction, hasRequest: typeof provider.request, ua: navigator.userAgent }));
    // eslint-disable-next-line no-console
    console.log("[solana] DEBUG transaction going to Phantom", JSON.stringify(describeTx(rawIn)));
  }
  const blankSigs = (bytes) => { const c = bytes.slice(); c.fill(0, 1, 1 + c[0] * 64); return c; };
  // Versioned transactions: try the Wallet Standard path first (falls back to the older paths below if it is not there
  // or fails for a reason other than the user declining).
  {
    const rawIn = base64ToBytes(transactionBase64);
    // (legacy transactions go this way too: Phantom may change them before signing, and this path is the one that lets us
    // read back the message it really signed)
    if (!(typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("eqNoWalletStandard"))) {
      try {
        const signedBytes = await signWithWalletStandard(blankSigs(rawIn), expectedAddress || signer, found.name);
        if (signedBytes) {
          const sentMsg = rawIn.slice(1 + rawIn[0] * 64);
          const got = signedBytes.slice(1 + signedBytes[0] * 64);
          const same = got.length === sentMsg.length && got.every((b, i) => b === sentMsg[i]);
          // eslint-disable-next-line no-console
          lastSignedMessage = same ? null : toBase64(got);
          return toBase64(signatureFromSignedTx(signedBytes, expectedAddress || signer));
        }
      } catch (e) {
        if (e && (e.code === 4001 || /reject|denied|cancel|declin/i.test(e.message || ""))) throw new Error("You declined the request in Phantom.");
        dumpErr("[solana] wallet-standard signing failed, trying the older paths", e);
      }
    }
  }
  let res;
  try {
    res = await provider.request({
      method: "signTransaction",
      params: { message: base58Encode(blankSigs(base64ToBytes(transactionBase64))) },
    });
  } catch (e) {
    const declined = (x) => x && (x.code === 4001 || /reject|denied|cancel/i.test(x.message || ""));
    if (declined(e)) throw new Error("You declined the request in Phantom.");
    // eslint-disable-next-line no-console
    dumpErr("[solana] request({signTransaction}) failed -> retrying with provider.signTransaction()", e);
    // Fallback: the injected signTransaction(tx) path, which Phantom handles for versioned (v0) transactions.
    // It only needs an object with `version` (v0) and serialize(); Phantom deserializes the result itself.
    try {
      const raw = blankSigs(base64ToBytes(transactionBase64));
      const isV0 = (raw[1 + raw[0] * 64] & 0x80) !== 0;
      if (typeof provider.signTransaction !== "function") throw e;
      // Phantom serializes via VersionedTransaction.prototype.serialize, which reads this.signatures and
      // this.message.serialize() and this.message.version, so mirror exactly that shape.
      const nSig = raw[0];
      const sigList = [];
      for (let k = 0; k < nSig; k++) sigList.push(raw.slice(1 + k * 64, 1 + (k + 1) * 64));
      const msgBytes = raw.slice(1 + nSig * 64);
      const txLike = isV0
        ? { version: 0, signatures: sigList, message: { version: 0, serialize: () => msgBytes }, serialize: () => raw }
        : { serialize: () => raw };
      const signed = await provider.signTransaction(txLike);
      captureSignedMessage(signed, raw);
      const sigs = signed && signed.signatures;
      const idx = signerIndexInTx(raw, expectedAddress || signer);
      const entry = sigs && idx >= 0 ? sigs[idx] : null;
      const b = entry ? toBytes(entry.signature || entry) : null;
      if (b && b.length === 64) return toBase64(b);
      const full = signed && typeof signed.serialize === "function" ? signed.serialize() : null;
      if (full && full.length > 64) return toBase64(signatureFromSignedTx(full, expectedAddress || signer));
      throw new Error("Phantom returned no usable signature.");
    } catch (e2) {
      if (declined(e2)) throw new Error("You declined the request in Phantom.");
      // eslint-disable-next-line no-console
      dumpErr("[solana] provider.signTransaction() failed too:", e2);
      throw new Error((e2 && e2.message) || (e && e.message) || "Phantom could not sign the transaction.");
    }
  }
  // eslint-disable-next-line no-console
  try {
    // eslint-disable-next-line no-console
    console.log("[solana] wallet reply (first 200 chars):", JSON.stringify(res).slice(0, 200));
  } catch (e) {}

  captureSignedMessage(res, base64ToBytes(transactionBase64));
  // Case 1: a signature (string or bytes) on its own.
  const direct = res && typeof res === "object" && !(res instanceof Uint8Array) && !Array.isArray(res) ? res.signature : null;
  if (direct) {
    const b = toBytes(direct);
    if (b && b.length === 64) return toBase64(b);
  }
  // Case 1b: `signatures`, one entry per required signer in transaction order (newer Phantom). Take ours.
  if (res && typeof res === "object" && Array.isArray(res.signatures)) {
    const idx = signerIndexInTx(base64ToBytes(transactionBase64), expectedAddress || signer);
    const entry = idx >= 0 ? res.signatures[idx] : null;
    const b = entry ? toBytes(entry) : null;
    if (b && b.length === 64) return toBase64(b);
  }
  // Case 2: the whole signed transaction (the reply itself, or a field of it).
  const candidates = [res, direct, res && res.transaction, res && res.signedTransaction, res && res.serializedTransaction, res && res.result];
  for (const c of candidates) {
    const b = c ? toBytes(c) : null;
    if (b && b.length > 64) return toBase64(signatureFromSignedTx(b, expectedAddress || signer));
  }
  const lens =
    res && typeof res === "object"
      ? Object.keys(res).map((k) => { const b = toBytes(res[k]); return k + (b ? ":" + b.length + "B" : ""); }).join(", ") || "empty"
      : String(res);
  throw new Error("Phantom did not return a usable signature (reply: " + lens + ").");
};
// ---- DIAGNOSTIC: Phantom's own "sign and send" path (Phantom signs AND broadcasts). Used only when
// localStorage.eqSignAndSend is set (see utils/solanaMarket.js). Resolves { signature } or throws Phantom's error.
// Whatever signatures the backend already put on the transaction are kept (nothing is blanked).
